import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    ListItemButton,
    ListItemText,
    Radio,
    Typography,
    Stack
} from "@mui/material/";
import LoadingButton from "@mui/lab/LoadingButton";
import { Close } from "@mui/icons-material";
import { strings } from "../../../../common/i18n";
import { enums } from "../../../../common/lib";
import { ErrorThemeProvider } from "../../../../common/styles/providers";
import Paper from "../Paper";
import styles from "./styles";
import { logToSentry } from "../../../../config/Sentry";

type Props = {
    onClose: Function,
    onSubmit: Function,
    readonly: boolean,
    value: any
};

/**
 * Audit Asset Reject Component
 */
export default ({ onClose, onSubmit, readonly, value }) => {
    const [reissue, setReissue] = useState(false);
    const [loading, setLoading] = useState(false);
    const { rejectionReasons } = useSelector((state) => state.typesList);
    const [rejectionReason, setRejectionReason] = useState(null);

    useEffect(() => {
        if (
            !rejectionReason &&
            value &&
            value.assetRejection &&
            value.assetRejection.length
        ) {
            const next = value.assetRejection[value.assetRejection.length - 1];
            setRejectionReason(next.rejectionReason);
        }
    }, [value]);

    useEffect(() => {
        if (value && value.reissuedStatus === enums.status.reissued.reissued) {
            setReissue(true);
        }
    }, [value]);

    const handleListItemClick = (next: number) => () =>
        setRejectionReason(next);

    /** On reissue asset  */
    const onReissue = () => {
        try {
            setLoading(true);
            setReissue(!reissue);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On reissue reissuing:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onReissueAsset"))
            );
        } finally {
            setLoading(false);
        }
    };

    /** On reject asset  */
    const onReject = async () => {
        try {
            setLoading(true);
            if (reissue) {
                await onSubmit(enums.status.reissued.reissued, rejectionReason);
            } else {
                await onSubmit(
                    value && value.reissuedStatus
                        ? enums.status.reissued.unresolved
                        : null,
                    rejectionReason
                );
            }
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On rejecting asset:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onRejectingAsset"))
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper
            HeaderComponent={
                <>
                    <Grid alignItems="center" container direction="row">
                        <Grid item xs={10}>
                            <Typography gutterBottom variant="h5">
                                {`${strings("chooseARejection")} ${strings(
                                    "reason"
                                )}`}
                            </Typography>
                            <Box sx={styles.flexGrow} />
                        </Grid>
                        <Grid item xs={2} align="right">
                            <IconButton onClick={onClose} size="small">
                                <Close />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <Divider />
                </>
            }
            BodyComponent={rejectionReasons.data.map((item) => (
                <ListItemButton
                    disabled={readonly}
                    key={item.id}
                    onClick={handleListItemClick(item.id)}
                    selected={item.id === rejectionReason}
                >
                    <Radio
                        disabled={readonly}
                        onClick={handleListItemClick(item.id)}
                        checked={item.id === rejectionReason}
                        value={item.name}
                    />
                    <ListItemText key={`text_${item.name}`}>
                        {item.name}
                    </ListItemText>
                </ListItemButton>
            ))}
            FooterComponent={
                <>
                    <Divider />
                    <FormControlLabel
                        color="secondary"
                        control={
                            <Checkbox
                                checked={reissue}
                                disabled={readonly}
                                onClick={onReissue}
                            />
                        }
                        label={strings("reissue")}
                    />
                    <Stack direction="row" spacing={2}>
                        <LoadingButton
                            loading={loading}
                            sx={styles.buttonStyle}
                            color="error"
                            onClick={onClose}
                            size="medium"
                            variant="outlined"
                        >
                            {strings("cancel")}
                        </LoadingButton>
                        <ErrorThemeProvider spacing={2}>
                            <LoadingButton
                                loading={loading}
                                sx={styles.buttonStyle}
                                color="error"
                                disabled={
                                    loading || readonly || !rejectionReason
                                }
                                onClick={onReject}
                                size="medium"
                                variant="contained"
                            >
                                {strings("rejectAsset")}
                            </LoadingButton>
                        </ErrorThemeProvider>
                    </Stack>
                </>
            }
        />
    );
};
